import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';

import { addItemDroppableId, MAX_TRANSPORT_UNIT_DEPTH, newSubUnitDroppableId } from '../../constants';
import { canNest, getAllowedUnitTypeOptions } from '../../helpers/unitTypeHierarchy';
import { UnitTypeOption } from '../../state/packingListReducer';
import { LineItemEntry, LineItemId, TransportUnitId, TransportUnitNode } from '../../types';
import { PlacedItem } from './PlacedItem';

type TransportUnitCardProps = {
  node: TransportUnitNode;
  position: string;
  level: number;
  lineItemsById: Record<LineItemId, LineItemEntry>;
  unitTypeOptions: Array<UnitTypeOption>;
  // The immediate parent's unit type, undefined at the top level. Narrows which types this
  // node's own dropdown offers, and flags the node when the parent cannot hold its type.
  parentUnitType?: string;
  onChangeUnitType: (tuId: TransportUnitId, unitType: string) => void;
  onDelete: (tuId: TransportUnitId) => void;
  onRemoveItem: (lineItemId: LineItemId, tuId: TransportUnitId) => void;
  selectedPlacedKeys: Set<string>;
  onTogglePlaced: (tuId: TransportUnitId, lineItemId: LineItemId) => void;
  onMoveToItem: (tuId: TransportUnitId, lineItemId: LineItemId) => void;
  onEditItemQuantity: (tuId: TransportUnitId, lineItemId: LineItemId) => void;
};

/**
 * The delete label is selected with a ternary inside t(); enumerate both
 * branches so the i18next-parser keeps them.
 *
 * i18n keys
 * t('webedi:packing_list.transport_unit.delete')
 * t('webedi:packing_list.transport_unit.delete_sub_unit')
 * t('webedi:packing_list.transport_unit.invalid_nesting', { parentUnitType, unitType })
 * t('webedi:packing_list.transport_unit.sscc', { sscc })
 * t('webedi:packing_list.transport_unit.sscc_pending')
 */
export const TransportUnitCard = ({
  node,
  position,
  level,
  lineItemsById,
  unitTypeOptions,
  parentUnitType,
  onChangeUnitType,
  onDelete,
  onRemoveItem,
  selectedPlacedKeys,
  onTogglePlaced,
  onMoveToItem,
  onEditItemQuantity,
}: TransportUnitCardProps) => {
  const { t } = useTranslation();
  const isContainer = node.children.length > 0;
  const isNested = level > 1;
  const dropdownOptions = getAllowedUnitTypeOptions(unitTypeOptions, { parentUnitType, keep: node.unitType });
  // Retyping a parent is never blocked by its children, so a child can end up in a parent that
  // cannot hold it. Flag it here instead; Apply still warns about every such pair.
  const isInvalidNesting = parentUnitType !== undefined && !canNest(parentUnitType, node.unitType);
  const labelOf = (unitType: string) => unitTypeOptions.find((option) => option.value === unitType)?.label ?? unitType;
  // A sub-unit can only be added when this unit may hold some type — placing one here with
  // nothing valid to offer would create a unit whose own type dropdown is stuck.
  const childUnitTypeOptions = getAllowedUnitTypeOptions(unitTypeOptions, { parentUnitType: node.unitType });
  const canAddSubUnit = level < MAX_TRANSPORT_UNIT_DEPTH && childUnitTypeOptions.length > 0;
  const items = node.contents
    .map((content) => ({ entry: lineItemsById[content.lineItemId], quantity: content.quantity }))
    .filter((item) => Boolean(item.entry));
  // The whole leaf card is the drop target, not just the small dashed strip, so an item dropped
  // anywhere on the unit lands in it. Containers hold no items directly (leaf-only invariant), so
  // they never accept an add-item drop. The inner new-sub-unit strip stays its own droppable;
  // pointerWithin prefers it when the pointer is released over it.
  const { setNodeRef, isOver } = useDroppable({ id: addItemDroppableId(node.id), disabled: isContainer });
  const isDropActive = isOver && !isContainer;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-lg border p-4 transition-colors',
        isDropActive ? 'border-procuros-green-600 bg-green-50' : 'border-gray-200 bg-white',
      )}
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-sm leading-5">
          <span className="font-bold text-gray-900">{t('webedi:packing_list.transport_unit.title', { position })}</span>
          {dropdownOptions.length > 0 ? (
            <Tooltip enabled={isInvalidNesting}>
              <TooltipTrigger>
                <span>
                  <Dropdown
                    options={dropdownOptions}
                    value={node.unitType}
                    onChange={(value) => onChangeUnitType(node.id, value)}
                    name={`unit-type-${node.id}`}
                    size="extra-small"
                    buttonClassName="rounded-3xl"
                    fieldState={isInvalidNesting ? 'warning' : undefined}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t('webedi:packing_list.transport_unit.invalid_nesting', {
                  parentUnitType: labelOf(parentUnitType ?? ''),
                  unitType: labelOf(node.unitType),
                })}
              </TooltipContent>
            </Tooltip>
          ) : null}
          {node.sscc ? (
            <span className="text-gray-900">{t('webedi:packing_list.transport_unit.sscc', { sscc: node.sscc })}</span>
          ) : (
            <span className="text-gray-400">{t('webedi:packing_list.transport_unit.sscc_pending')}</span>
          )}
        </div>
        <button
          type="button"
          aria-label={t(
            isNested
              ? 'webedi:packing_list.transport_unit.delete_sub_unit'
              : 'webedi:packing_list.transport_unit.delete',
          )}
          onClick={() => onDelete(node.id)}
        >
          <XMarkIcon className="size-4 text-gray-400 hover:text-gray-600" />
        </button>
      </div>

      {isContainer ? (
        <div className="mt-3 flex flex-col gap-3">
          {node.children.map((child, index) => (
            <TransportUnitCard
              key={child.id}
              node={child}
              position={`${position}.${index + 1}`}
              level={level + 1}
              lineItemsById={lineItemsById}
              unitTypeOptions={unitTypeOptions}
              parentUnitType={node.unitType}
              onChangeUnitType={onChangeUnitType}
              onDelete={onDelete}
              onRemoveItem={onRemoveItem}
              selectedPlacedKeys={selectedPlacedKeys}
              onTogglePlaced={onTogglePlaced}
              onMoveToItem={onMoveToItem}
              onEditItemQuantity={onEditItemQuantity}
            />
          ))}
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {items.map((item) => (
            <PlacedItem
              key={item.entry.id}
              entry={item.entry}
              quantity={item.quantity}
              tuId={node.id}
              isSelected={selectedPlacedKeys.has(`${node.id}:${item.entry.id}`)}
              onToggleSelect={() => onTogglePlaced(node.id, item.entry.id)}
              onRemoveItem={onRemoveItem}
              onMoveTo={() => onMoveToItem(node.id, item.entry.id)}
              onEditQuantity={() => onEditItemQuantity(node.id, item.entry.id)}
            />
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-col gap-2">
        {!isContainer ? (
          <div
            className={cn(
              'rounded-md border border-dashed px-3 py-2 text-center text-xs',
              isDropActive ? 'border-procuros-green-600 text-procuros-green-700' : 'border-gray-300 text-gray-400',
            )}
          >
            {t('webedi:packing_list.transport_unit.add_item')}
          </div>
        ) : null}
        {canAddSubUnit ? (
          <Dropzone id={newSubUnitDroppableId(node.id)} label={t('webedi:packing_list.transport_unit.new_sub_unit')} />
        ) : null}
      </div>
    </div>
  );
};

const Dropzone = ({ id, label }: { id: string; label: string }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-md border border-dashed px-3 py-2 text-center text-xs',
        isOver ? 'border-procuros-green-600 bg-green-50' : 'border-gray-300 text-gray-400',
      )}
    >
      {label}
    </div>
  );
};
